/**
 * 「充值运营」五页共用的小控件与格式化：筛选下拉、Alice 风格弹窗、美元与数字显示。
 *
 * 背景：Alice 的这几页大量用内联样式的下拉和 .modal-mask 弹窗，kit.tsx 里没有对应组件；
 *   共用的 kit-runtime2.tsx 属于另一组页面，这里自己写，不依赖它。
 * 设计意图：弹窗沿用 Alice 的 .modal-mask / .modal / .modal-footer 类名（样式在 alice-admin.css），
 *   页面只写表单；Esc 关闭，提交中不可关闭（避免请求发出后界面消失）。
 * 关键约束：不改 kit.tsx 的既有导出；订单金额后端给的是美元字符串，这里只做显示换算，不参与计算。
 */
import { useEffect, type CSSProperties, type ReactNode, type SelectHTMLAttributes } from 'react'

export const selectStyle: CSSProperties = {
  padding: '8px 28px 8px 10px',
  fontSize: 13,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
}

export function ToolSelect({
  options,
  style,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  return (
    <select {...rest} style={{ ...selectStyle, ...style }}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/** Alice 的 .modal-mask 弹窗；open 为 false 时不渲染。 */
export function AliceModal({
  open,
  title,
  children,
  footer,
  onClose,
  busy = false,
  maxWidth = 520,
}: {
  open: boolean
  title: ReactNode
  children: ReactNode
  footer: ReactNode
  onClose: () => void
  busy?: boolean
  maxWidth?: number
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, onClose])
  if (!open) return null
  return (
    <div className="modal-mask open">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth }}>
        <div className="modal-title">{title}</div>
        {children}
        <div className="modal-footer">{footer}</div>
      </div>
    </div>
  )
}

/** 美元字符串（"12.34"）或数字按当前语言显示为 $12.34。 */
export function fmtUsdStr(v: string | number | null | undefined, tag: string): string {
  const n = Number(v ?? 0)
  return n.toLocaleString(tag, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function fmtNum(v: number | string | null | undefined, tag: string): string {
  return Number(v ?? 0).toLocaleString(tag)
}

/** 花瓣数换算成美元面值（1 美元 = 1,000 花瓣）显示，如 12,000 ($12.00)。 */
export function fmtPetalsUsd(petals: number, tag: string): string {
  return `${fmtNum(petals, tag)} ($${(petals / 1000).toLocaleString(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
}

/** 比例字符串（"12.34"）显示为 12.34%。 */
export function pct(v: string | number | null | undefined): string {
  return `${Number(v ?? 0).toFixed(2)}%`
}

/** ISO 时间转 datetime-local 输入框的值（本地时间）。 */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

/** datetime-local 的值转带时区的 ISO；留空返回空串（后端按「不限」处理）。 */
export function fromLocalInput(v: string): string {
  return v ? new Date(v).toISOString() : ''
}

/** Token 数的简写：1.2K、3.5M、2B。 */
export function fmtTokens(n: number): string {
  if (n >= 1e9) return `${+(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `${+(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `${+(n / 1e3).toFixed(2)}K`
  return String(n)
}
